import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Apple, CalendarDays, AlertTriangle, UserCircle2, LogOut, Leaf, Activity, FileText, Bell, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const baseNav = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", id: "nav-dashboard" },
  { to: "/foods", icon: Apple, label: "Foods", id: "nav-foods" },
  { to: "/meal-plan", icon: CalendarDays, label: "Meal Plan", id: "nav-meal-plan" },
  { to: "/tracking", icon: Activity, label: "Tracking", id: "nav-tracking" },
  { to: "/interactions", icon: AlertTriangle, label: "Interactions", id: "nav-interactions" },
  { to: "/reminders", icon: Bell, label: "Reminders", id: "nav-reminders" },
  { to: "/reports", icon: FileText, label: "Report", id: "nav-reports" },
  { to: "/profile", icon: UserCircle2, label: "Profile", id: "nav-profile" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/me").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  const navItems = isAdmin
    ? [...baseNav, { to: "/admin", icon: ShieldCheck, label: "Admin", id: "nav-admin" }]
    : baseNav;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F4FBF6]">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white/80 backdrop-blur border-r border-emerald-100 sticky top-0 h-screen">
        <div className="px-6 py-6 flex items-center gap-2 border-b border-emerald-100">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-lg text-slate-900 leading-none">NutriGuide</div>
            <div className="text-xs text-slate-500 mt-0.5">Eat for your health</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:bg-emerald-50/50 hover:text-emerald-700"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-emerald-100">
          <div className="px-3 py-2 mb-2 rounded-xl bg-sky-50">
            <div className="text-xs text-sky-700 font-medium">Signed in</div>
            <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            className="w-full rounded-xl border-emerald-200 text-slate-700 hover:bg-emerald-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur border-b border-emerald-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-bold text-base">NutriGuide</span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn-mobile">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <main className="flex-1 min-w-0 md:py-0 pt-14 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav (scrolls horizontally) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-emerald-100">
        <div className="flex overflow-x-auto no-scrollbar">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.id}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[10px] flex-shrink-0 min-w-[68px] ${
                  isActive ? "text-emerald-600" : "text-slate-500"
                }`
              }
            >
              <n.icon className="w-4 h-4 mb-0.5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}
